import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWrapped } from '../api/client';
import type { Source, WrappedResponse } from '../api/types';

/**
 * The intro's data, fetched alongside the recommendations. It is optional by
 * design: `failed` means "skip the intro", never "show an error", because the
 * rider asked for recommendations and those are what the page is for.
 */
export type WrappedState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; data: WrappedResponse }
  | { status: 'failed' };

export function useWrapped() {
  const [state, setState] = useState<WrappedState>({ status: 'idle' });
  const inFlight = useRef<AbortController | null>(null);
  useEffect(() => () => inFlight.current?.abort(), []);

  const load = useCallback((username: string, source: Source) => {
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    setState({ status: 'loading' });
    // Sent beside the stream, so it may wait as long as that does. If this were
    // turned away first, both would settle for the other site's account.
    fetchWrapped({ username: username.trim(), source, patient: true }, controller.signal)
      .then((data) => !controller.signal.aborted && setState({ status: 'ready', data }))
      .catch(() => !controller.signal.aborted && setState({ status: 'failed' }));
  }, []);

  const reset = useCallback(() => {
    inFlight.current?.abort();
    setState({ status: 'idle' });
  }, []);

  return { state, load, reset };
}
